import { useUploadStore } from "@components/FileUpload/uploadStore";
import { useNotification } from "@context/NotificationContext";
import { getSubmitErrorMessage } from "@features/gatepass/hooks/useSubmitGatepassRequest.tsx";
import { mapSecurityClearanceFormDataToPayload } from "@features/port-security/components/utils";
import {
  VISIT_DETAILS_DOCUMENT_FIELDS,
  type VisitDetailsDocumentField,
} from "@features/port-security/constants";
import { getPortSecurityFormSchema } from "@features/port-security/schema/portSecurityFormSchema";
import type { PortSecurityFormData } from "@features/port-security/schema/portSecurityFormSchema";
import { usePortSecurityFormStore } from "@features/port-security/store/usePortSecurityForm.store";
import { applySecurityClearanceApiValidationErrors } from "@features/port-security/utils/securityClearanceFormValidation";
import { readSessionRenewalContext, useAuthStore } from "@persisted-state/auth/auth.store";
import type { SecurityClearanceRequestPayload } from "@services/security-clearance/types";
import { submitSecurityClearanceRequest } from "@services/securityClearanceApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { cleanSecurityClearancePayload } from "@utils/payloadClean";
import { t } from "i18next";
import type { UseFormSetError } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

interface UseSecurityClearanceProps {
  formValues: z.infer<ReturnType<typeof getPortSecurityFormSchema>>;
  resetForm: () => void;
  securityClearanceRenewID?: string;
  clearVisitDetailsDocumentFormValues?: (fields: readonly VisitDetailsDocumentField[]) => void;
  setError?: UseFormSetError<PortSecurityFormData>;
  onSubmitError?: (error: unknown) => void;
  onApiValidationErrors?: (fields: (keyof PortSecurityFormData)[]) => void;
}

export interface UseSecurityClearanceResult {
  onSubmit: (submittedFormValues?: PortSecurityFormData) => Promise<void>;
}

const isInternalServerError = (error: unknown): boolean =>
  (error as { response?: { status?: number } })?.response?.status === 500;

const clearVisitDetailsDocumentsFromStore = () => {
  const clearedDocumentFields = VISIT_DETAILS_DOCUMENT_FIELDS.reduce(
    (acc, field) => ({
      ...acc,
      [field]: [],
    }),
    {} as Pick<PortSecurityFormData, VisitDetailsDocumentField>,
  );

  usePortSecurityFormStore.getState().setAll(clearedDocumentFields);
};

export const useSecurityClearance = (
  props: UseSecurityClearanceProps,
): UseSecurityClearanceResult => {
  const {
    resetForm,
    formValues,
    clearVisitDetailsDocumentFormValues,
    setError,
    onSubmitError,
    onApiValidationErrors,
  } = props;

  const navigate = useNavigate();
  const { notify } = useNotification();
  const clearUploadStore = useUploadStore((state) => state.clearAll);
  const clearUploadFilesForKey = useUploadStore((state) => state.clearFilesForKey);

  const securityClearance = useMutation<unknown, unknown, SecurityClearanceRequestPayload>({
    mutationFn: (payload) => {
      const state = useAuthStore.getState();
      const parsedUrl = new URLSearchParams(window.location.search);
      const sessionContext = readSessionRenewalContext();
      const currentRenewID = state.securityClearanceRenewID || sessionContext.renewalId;
      const requestStatus = state.securityClearanceRequestStatus || sessionContext.requestStatus;
      const resolvedRenewID =
        currentRenewID ||
        parsedUrl.get("renewalId") ||
        parsedUrl.get("existingSecurityClearanceId") ||
        undefined;
      const resolvedStatus = requestStatus || parsedUrl.get("requestStatus") || null;
      const isResolvedResubmission = resolvedStatus === "PENDING_RESUBMISSION";

      return runTransaction(
        {
          module: "security_clearance",
          screen: "SecurityClearanceRequest",
          action: isResolvedResubmission
            ? "resubmit_security_clearance"
            : resolvedRenewID
              ? "renew_security_clearance"
              : "submit_security_clearance",
          entityType: "security_clearance",
        },
        (transactionId) =>
          submitSecurityClearanceRequest({
            service: "bff-gatepass",
            securityClearancePayload: payload,
            securityClearanceRenewID: resolvedRenewID,
            securityClearanceRequestStatus: resolvedStatus,
            ...(transactionId ? { transactionId } : {}),
          }),
      );
    },
    onSuccess: () => {
      resetForm();
      navigate("/port-security");
      notify({
        title: t("security-clearance.notifications.sc-request-submitted-success-title"),
        type: "success",
      });

      clearUploadStore();
      useAuthStore.getState().setSecurityClearanceRenewID(null);
    },
    onError: (error) => {
      if (isInternalServerError(error)) {
        clearVisitDetailsDocumentsFromStore();
        VISIT_DETAILS_DOCUMENT_FIELDS.forEach((field) => clearUploadFilesForKey(field));
        clearVisitDetailsDocumentFormValues?.(VISIT_DETAILS_DOCUMENT_FIELDS);
      }

      if (setError) {
        const appliedFields = applySecurityClearanceApiValidationErrors(error, setError);
        if (appliedFields.length > 0) {
          onApiValidationErrors?.(appliedFields);
        }
      }

      notify({
        title: t("security-clearance.notifications.sc-request-submitted-error-title"),
        description:
          getSubmitErrorMessage(error) ||
          t("security-clearance.notifications.sc-request-submitted-error-description"),
        type: "error",
      });
      console.error("Error submitting security clearance request:", error);
      onSubmitError?.(error);
    },
  });

  const onSubmit = async (submittedFormValues?: PortSecurityFormData): Promise<void> => {
    if (useUploadStore.getState().hasAnyUploadInFlight()) {
      onSubmitError?.(new Error("upload in flight"));
      return;
    }

    const payload: SecurityClearanceRequestPayload = mapSecurityClearanceFormDataToPayload(
      submittedFormValues ?? formValues,
    );
    const cleanedPayload = cleanSecurityClearancePayload(payload);
    securityClearance.mutate(cleanedPayload);
  };
  return { onSubmit };
};
